import {client} from '../../contacts-api-integration';
import {Contact} from "../types.ts";

export const fetchRecords = async ({ limit, nextToken, statusFilter }: { limit?: number; nextToken?: string | null; statusFilter?: string | null } = {}): Promise<{ items: Contact[]; nextToken?: string }> => {
    const filter = statusFilter ? { status: statusFilter } : undefined;
    const response: any = await client.graphql({
        query: `query ListContacts($filter: ContactsFilterInput, $limit: Int, $nextToken: String) {
            listContacts(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                    id
                    firstName
                    lastName
                    email
                    phone
                    company
                    position
                    notes
                    status
                    createdByUsername
                    managedByUsername
                    created
                    updated
                }
                nextToken
            }
        }`,
        variables: { filter, limit, nextToken }
    });
    return { 
        items: response.data.listContacts.items,
        nextToken: response.data.listContacts.nextToken 
    };
}

export const fetchContactByEmail = async (email: string): Promise<Contact | null> => {
    const response: any = await client.graphql({
        query: `query ListContacts($filter: ContactsFilterInput, $limit: Int) {
            listContacts(filter: $filter, limit: $limit) {
                items {
                    id
                    firstName
                    lastName
                    email
                }
            }
        }`,
        variables: { filter: { email }, limit: 1 }
    });
    const items = response.data.listContacts.items;
    return items.length > 0 ? items[0] : null;
}

export const fetch = async (id: string): Promise<{ data: Contact }> => {
    const response: any = await client.graphql({
        query: `query GetContact($id: ID!) {
            getContact(id: $id) {
                id
                firstName
                lastName
                email
                phone
                company
                position
                notes
                status
                createdByUsername
                managedByUsername
                created
                updated
            }
        }`,
        variables: { id }
    });
    return { data: response.data.getContact };
}

export const add = async (payload: Contact): Promise<Contact> => {
    const { id, managedByUsername, created, updated, ...rest } = payload;
    // Filter out null and undefined values
    const input = Object.entries(rest).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            acc[key] = value;
        }
        return acc;
    }, {} as any);
    
    const response: any = await client.graphql({
        query: `mutation CreateContact($input: CreateContactInput!) {
            createContact(input: $input) {
                id
                firstName
                lastName
                email
                phone
                company
                position
                notes
                status
                createdByUsername
                managedByUsername
                created
                updated
            }
        }`,
        variables: { input }
    });
    return response.data.createContact;
}

export const update = async (id: string, record: Contact): Promise<Contact> => {
    // Whitelist to the fields UpdateContactInput accepts. Blacklisting (destructure
    // out a few keys, spread ...rest) silently leaks any field the record grows —
    // e.g. `updated`/`recordType` — and AppSync then rejects the WHOLE mutation with
    // "field not defined for input object type 'UpdateContactInput'", writing nothing
    // (FNP-764, same class as the FNP-760 alias fix). Whitelist, never blacklist.
    // Server-managed audit fields (updated/updatedByUsername) are set by the resolver.
    const whitelisted: Record<string, unknown> = {
        firstName: record.firstName,
        lastName: record.lastName,
        email: record.email,
        phone: record.phone,
        company: record.company,
        position: record.position,
        notes: record.notes,
        status: record.status,
    };
    // Preserve the prior behaviour: omit null/undefined/empty values from the payload.
    const input = Object.entries(whitelisted).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, unknown>);
    input.id = id;

    const response: any = await client.graphql({
        query: `mutation UpdateContact($input: UpdateContactInput!) {
            updateContact(input: $input) {
                id
                firstName
                lastName
                email
                phone
                company
                position
                notes
                status
                createdByUsername
                managedByUsername
                created
                updated
            }
        }`,
        variables: { input }
    });
    return response.data.updateContact;
}

export const deleteRecord = async (id: string) => {
    await client.graphql({
        query: `mutation DeleteContact($input: DeleteContactInput!) {
            deleteContact(input: $input)
        }`,
        variables: { input: { id } }
    });
    return id;
}